import { useQuery, useQueryClient } from '@tanstack/react-query';

import useAuth from '@/hooks/useAuth';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';
import { useRouteMatcher } from '@/utils/url';

import Constants from '@/constants';

import { processUserName } from '@/services/modelHelper';

import type {
  CertificatesDataOptions,
  CertificatesDataResult,
} from './certificates.types';
import {
  isActiveCertificate,
  selectIntersectionCount,
  selectPlanCertificateCount,
} from './utils';

import {
  getActivePlanAssignmentsOptions,
  getManagedPlansOptions,
  getUsersForLocationsOptions,
} from '@/_generated/api-clients/xp';

const managedPlansListOptions = getManagedPlansOptions({
  query: { status: 'ACTIVE' },
});

const myPlansListOptions = getActivePlanAssignmentsOptions();

/**
 * Certificate data for `/plans` routes.
 *
 * On **My Plans** routes, reads from `getActivePlanAssignmentsOptions` (the
 * TM-accessible list cache). On **Manage Plans** routes, reads from
 * `getManagedPlansOptions` (the manager list cache).
 * User names come from the `getUsersForLocations` cache (Manage Plans) or
 * `auth.fullName` (My Plans).
 */
export const usePlansCertificatesData = ({
  teamMemberId,
  planId,
  enabled = true,
}: CertificatesDataOptions): CertificatesDataResult => {
  const queryClient = useQueryClient();
  const { data: auth } = useAuth();

  const routeMatches = useRouteMatcher();
  const isMyPlansRoute = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingMyPlansPath,
  );

  const hasBothIds = !!teamMemberId && isValidPlanId(planId ?? undefined);
  const hasValidPlan = isValidPlanId(planId ?? undefined);
  const locationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  // ============================================================
  // MY PLANS — count from getActivePlanAssignments list cache.
  // Uses assignment status (active completion state), not
  // latestSummaryReports (historical records).
  // ============================================================

  const {
    data: myPlanCount,
    isFetching: isFetchingMyPlan,
    isSuccess: isMyPlanReady,
  } = useQuery({
    ...myPlansListOptions,
    enabled: enabled && isMyPlansRoute && hasBothIds,
    select: data => {
      const userPlan = data.find(up => up.plan?.id === planId);
      const assignment = userPlan?.assignments?.find(
        a => a.userId === teamMemberId,
      );
      return assignment?.status === 'COMPLETED' ? 1 : 0;
    },
  });

  // ============================================================
  // MANAGE PLANS — single query, select routes by hasBothIds
  // ============================================================

  const {
    data: managedCount,
    isFetching: isFetchingManaged,
    isSuccess: isManagedReady,
  } = useQuery({
    ...managedPlansListOptions,
    enabled: enabled && !isMyPlansRoute && hasValidPlan,
    select: data =>
      hasBothIds
        ? selectIntersectionCount(data, planId, teamMemberId, false)
        : selectPlanCertificateCount(data, planId),
  });

  // ============================================================
  // LAZY CERTIFICATE FETCH
  // ============================================================

  const resolveUserName = async (userId: string): Promise<string> => {
    if (locationsWithAtLeastTrainer.length === 0) {
      return auth?.fullName ?? '';
    }
    const users = await queryClient.ensureQueryData(
      getUsersForLocationsOptions({
        query: { locationNumbers: locationsWithAtLeastTrainer },
      }),
    );
    const user = users.find(u => u.adId === userId);
    return processUserName(user);
  };

  const fetchCertificates = async () => {
    if (!planId || !isValidPlanId(planId)) return [];

    // My Plans: use assignment status (active completion state)
    if (isMyPlansRoute) {
      const userPlans = await queryClient.ensureQueryData(myPlansListOptions);
      const userPlan = userPlans.find(up => up.plan?.id === planId);
      if (!userPlan?.plan) return [];

      const planName = itemByLanguage(userPlan.plan.name) ?? '';
      const completedAssignments =
        userPlan.assignments?.filter(
          a =>
            (!teamMemberId || a.userId === teamMemberId) &&
            a.status === 'COMPLETED',
        ) ?? [];

      return completedAssignments.map(a => ({
        planName,
        teamMemberName: auth?.fullName ?? '',
        completionDate: a.latestCompletionReport?.completionDate ?? '',
        expirationDate: a.latestCompletionReport?.expirationDate ?? '',
      }));
    }

    // Manage Plans: use latestSummaryReports with compliance awareness
    const plans = await queryClient.ensureQueryData(managedPlansListOptions);
    const plan = plans.find(p => p.id === planId);
    if (!plan) return [];

    const planName = itemByLanguage(plan.name) ?? '';

    const activeSummaries =
      plan.latestSummaryReports?.filter(
        r =>
          (!teamMemberId || r.userId === teamMemberId) &&
          isActiveCertificate(r.status, r.latestCompletionReport, false),
      ) ?? [];

    const certs = await Promise.all(
      activeSummaries.map(async r => ({
        planName,
        teamMemberName: r.userId ? await resolveUserName(r.userId) : '',
        completionDate: r.latestCompletionReport?.completionDate ?? '',
        expirationDate: r.latestCompletionReport?.expirationDate ?? '',
      })),
    );

    return certs.toSorted((a, b) =>
      a.teamMemberName.localeCompare(b.teamMemberName),
    );
  };

  // ============================================================
  // DERIVED STATE
  // ============================================================

  const certificateCount = isMyPlansRoute
    ? (myPlanCount ?? 0)
    : (managedCount ?? 0);

  const isFetchingCount = isMyPlansRoute ? isFetchingMyPlan : isFetchingManaged;

  const isCountReady = isMyPlansRoute ? isMyPlanReady : isManagedReady;

  return {
    certificateCount,
    isFetchingCount,
    isCountReady,
    fetchCertificates,
  };
};
